import React from 'react';
import fetch from 'utilities/fetch';
import { paths } from 'config/path_helpers';
import { YourClassesContextInterface } from './types';

const YourClassesContext = React.createContext<YourClassesContextInterface | null>(null);

export const useYourClassesContext = () => {
  const context = React.useContext(YourClassesContext) as YourClassesContextInterface;
  if (context === undefined) {
    throw new Error(
      'You must wrap your page with <YourClassesProvider /> in order to useYourClassesContext().'
    );
  }
  return context;
};

export const YourClassesProvider: React.FC = ({ children }) => {
  const cache = React.useRef({});
  const [data, setData] = React.useState({
    widget: {
      upcoming: []
    },
    upcoming: {
      thisWeek: [],
      nextWeek: [],
      allFuture: []
    },
    recent: {
      thisWeek: [],
      lastWeek: [],
      previousWeeks: [
        {
          weekOf: 'tomorrow',
          liveClassSessions: []
        },
        {
          weekOf: 'now',
          liveClassSessions: []
        }
      ]
    },
    registered: {
      upcoming: [],
      past: []
    },
    yourClasses: []
  });
  const [openRegistrationFormModal, setOpenRegistrationFormModal] = React.useState(false);
  const [selectedClassData, setSelectedClassData] = React.useState(null);
  const [formState, setFormState] = React.useState<YourClassesContextInterface['formState']>('register');
  const [isRegisteringClass, setRegisteringClass] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [subsection, setSubsection] = React.useState('upcoming');

  const fetchData = React.useCallback(
    (subsection, refresh = false) => {
      if (cache.current[subsection] && !refresh) {
        const cacheData = cache.current[subsection];
        setData({ ...data, [subsection]: cacheData });
      } else {
        if (!refresh) {
          setIsLoading(true);
        }
        fetch(paths.api.liveClassSessions({ page: subsection })).then((response) => {
          setData({ ...data, [subsection]: response });
          cache.current[subsection] = response;
          setIsLoading(false);
          return response;
        });
      }
    },
    [data]
  );

  const registerLiveClassSession = React.useCallback(
    (classId: number) => {
      setRegisteringClass(true);
      fetch(paths.api.liveClassRegistrations(), {
        method: 'POST',
        body: JSON.stringify({
          liveClassSessionId: classId
        })
      }).then(() => {
        setRegisteringClass(false);
        setFormState('registered');
        cache.current[subsection] = null;
        fetchData(subsection);
      });
    },
    [subsection]
  );

  const toggleOpenRegistrationFormModal = React.useCallback(
    (classData) => {
      setSelectedClassData(classData);
      setFormState('register');
      setOpenRegistrationFormModal(!openRegistrationFormModal);
    },
    [openRegistrationFormModal]
  );

  const providerValue = React.useMemo(
    () => ({
      data,
      fetchData,
      openRegistrationFormModal,
      toggleOpenRegistrationFormModal,
      selectedClassData,
      formState,
      registerLiveClassSession,
      isRegisteringClass,
      isLoading,
      setSubsection
    }),
    [
      data,
      fetchData,
      openRegistrationFormModal,
      toggleOpenRegistrationFormModal,
      selectedClassData,
      formState,
      registerLiveClassSession,
      isRegisteringClass,
      isLoading,
      setSubsection
    ]
  );

  return <YourClassesContext.Provider value={providerValue}>{children}</YourClassesContext.Provider>;
};
